import { retryUnlessClientError } from '@brush/api-client';
import { AuthProvider, RequireAuth } from '@brush/auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { DevApiProvider, MsalApiProvider } from './api';
import { App } from './App';
import { apiScope, authDisabled, msalConfig } from './authConfig';
import './index.css';

const queryClient = new QueryClient({
  // A 4xx is an answer and is never asked again; anything else once (#128, `retryUnlessClientError`). A hook
  // stating its own `retry` still wins.
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: retryUnlessClientError } },
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root not found.');

// Dev: skip MSAL, talk to the dev-bypass API. Prod: wrap in MSAL + real tokens.
const tree: ReactNode = authDisabled ? (
  <QueryClientProvider client={queryClient}>
    <DevApiProvider>
      <App />
    </DevApiProvider>
  </QueryClientProvider>
) : (
  <AuthProvider config={msalConfig}>
    <RequireAuth appName="Brush Admin" variant="light" scopes={apiScope ? [apiScope] : []}>
      <QueryClientProvider client={queryClient}>
        <MsalApiProvider>
          <App />
        </MsalApiProvider>
      </QueryClientProvider>
    </RequireAuth>
  </AuthProvider>
);

createRoot(container).render(<StrictMode>{tree}</StrictMode>);
